import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

// Custom plugin to add margin between legend and chart
export const customMarginPlugin = {
    id: 'customMarginPlugin',
    beforeInit: function (chart) {
        const originalFit = chart.legend.fit;
        chart.legend.fit = function fit() {
            originalFit.bind(chart.legend)();
            this.height += chartsLegendMargin; // Adds the extra margin
        };
    },
};
export const chartsLegendMargin = 25;

// Severity styling for the anomaly tooltip's tag list.
const ANOMALY_SEVERITY_STYLE = {
    critical: { color: '#ff7875', label: 'Critical', rank: 3 },
    warning: { color: '#ffc53d', label: 'Warning', rank: 2 },
    info: { color: '#69b1ff', label: 'Info', rank: 1 },
    normal: { color: '#bfbfbf', label: 'Normal', rank: 0 },
};

// The expected/margin band edges are datasets with fixed labels; hovering an
// edge line shows that edge's own value for the hour.
const BAND_LINE_BY_DATASET_LABEL = {
    'Lower Bound': 'Expected lower',
    'Expected Range': 'Expected upper',
    'Margin Lower': 'Margin lower',
    'Margin Zone': 'Margin upper',
};

// escape user-provided tag names before injecting into the tooltip HTML
const escapeHtml = (str) =>
    String(str).replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));

// Hiding must also disable pointer events: an invisible div that still catches
// the mouse leaves a dead zone over the chart (and an I-beam cursor).
const hideTooltipEl = (el) => {
    el.style.opacity = 0;
    el.style.pointerEvents = 'none';
    el._hovering = false;
};

// External HTML tooltip for the anomaly chart: the default canvas tooltip can't
// scroll, so long anomalous-tag lists would get clipped.
const anomalyTooltipHandler = (context) => {
    const { chart, tooltip } = context;
    const parent = chart.canvas.parentNode;
    // The tooltip is absolutely positioned inside this parent, so it must be a
    // positioning context (Chart.js renders a bare <canvas> with no wrapper).
    if (getComputedStyle(parent).position === 'static') {
        parent.style.position = 'relative';
    }

    let el = parent.querySelector(':scope > .anomaly-chart-tooltip');
    if (!el) {
        el = document.createElement('div');
        el.className = 'anomaly-chart-tooltip';
        el.style.position = 'absolute';
        el.style.pointerEvents = 'none';
        el.style.cursor = 'default';
        el.style.zIndex = 20;
        // Style matches the default Chart.js canvas tooltip used by the other
        // charts (same background, radius, padding, font — and no drop shadow).
        el.style.background = 'rgba(0,0,0,0.8)';
        el.style.color = '#fff';
        el.style.borderRadius = '6px';
        el.style.padding = '6px';
        el.style.font = "12px 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif";
        el.style.transition = 'opacity 0.12s ease';
        el.style.opacity = 0;
        // Keep the tooltip open while the pointer is over it so the list is scrollable.
        el.addEventListener('mouseenter', () => {
            clearTimeout(el._hideTimer);
            el._hovering = true;
        });
        el.addEventListener('mouseleave', () => {
            hideTooltipEl(el);
        });
        parent.appendChild(el);
    }

    // Chart wants to hide (pointer left the point). Delay so the pointer can travel
    // into the tooltip to scroll; cancel the hide if it lands on the tooltip.
    if (tooltip.opacity === 0) {
        clearTimeout(el._hideTimer);
        el._hideTimer = setTimeout(() => {
            if (!el._hovering) hideTooltipEl(el);
        }, 350);
        return;
    }

    const dp = (tooltip.dataPoints || []).find(
        (d) => d.dataset && d.dataset.anomalyTagNames,
    );
    const bandDp = dp ? null : (tooltip.dataPoints || []).find(
        (d) => d.dataset && BAND_LINE_BY_DATASET_LABEL[d.dataset.label],
    );
    if (!dp && !bandDp) {
        hideTooltipEl(el);
        return;
    }
    const title = (tooltip.title && tooltip.title[0]) || '';

    let html = `<div style="font-weight:600;margin-bottom:6px;">${escapeHtml(title)}</div>`;

    if (bandDp) {
        const name = BAND_LINE_BY_DATASET_LABEL[bandDp.dataset.label];
        const value = Math.round(bandDp.parsed.y);
        html += `<div>${name}: <strong>${value.toLocaleString()}</strong></div>`;
    }

    if (dp) {
        const value = Math.round(dp.parsed.y);
        html += `<div style="margin-bottom:8px;">${escapeHtml(dp.dataset.label || 'Actual Count')}: `
            + `<strong>${value.toLocaleString()}</strong></div>`;

        const tags = (dp.dataset.anomalyTagNames[dp.dataIndex] || [])
            .slice()
            .sort((a, b) => {
                const ra = (ANOMALY_SEVERITY_STYLE[a.severity] || ANOMALY_SEVERITY_STYLE.normal).rank;
                const rb = (ANOMALY_SEVERITY_STYLE[b.severity] || ANOMALY_SEVERITY_STYLE.normal).rank;
                return rb - ra;
            });

        if (tags.length) {
            const rows = tags.map((t) => {
                const sev = ANOMALY_SEVERITY_STYLE[t.severity] || ANOMALY_SEVERITY_STYLE.normal;
                return `<div style="display:flex;align-items:center;gap:8px;padding:3px 0;">`
                    + `<span style="flex:0 0 auto;width:8px;height:8px;border-radius:50%;background:${sev.color};"></span>`
                    + `<span style="flex:1 1 auto;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(t.name)}</span>`
                    + `</div>`;
            }).join('');
            html += `<div style="font-weight:600;margin-bottom:4px;">${tags.length} anomalous tag${tags.length > 1 ? 's' : ''}</div>`
                + `<div class="anomaly-tooltip-tag-list" style="max-height:240px;overflow-y:auto;min-width:260px;padding-right:4px;">${rows}</div>`;
        }
    }

    el.innerHTML = html;

    // Only catch the mouse when the tag list overflows and needs scrolling;
    // otherwise let hovers pass through so neighbouring points stay reachable.
    const list = el.querySelector('.anomaly-tooltip-tag-list');
    const scrollable = !!list && list.scrollHeight > list.clientHeight;
    el.style.pointerEvents = scrollable ? 'auto' : 'none';
    if (!scrollable) el._hovering = false;

    // Position within the chart, clamped so the tooltip stays on-screen.
    el.style.opacity = 1;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    let left = tooltip.caretX + 12;
    if (left + w > chart.width) left = tooltip.caretX - w - 12;
    if (left < 0) left = 4;
    let top = tooltip.caretY - h / 2;
    if (top < 4) top = 4;
    if (top + h > chart.height) top = Math.max(4, chart.height - h - 4);
    el.style.left = left + 'px';
    el.style.top = top + 'px';
};

export const errorsChartOptions = (dashboardType, maxValue) => {
    let maxIndex = {};
    const ticksObj = {
        font: {
            family: "Inter"
        },
    };

    if (maxValue < 10) {
        maxIndex = { max: 10 };
        ticksObj.stepSize = 1;
    }

    return {
        responsive: true, // Set to true to allow the chart to resize with the container
        maintainAspectRatio: false, // Set to false to allow the chart to have its own aspect ratio
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: dashboardType === "error-monitoring" ? 'Error Percentage (%)' : 
                          dashboardType === "site-performance" ? 'Average execution time (ms)' : 'Total Calls',
                },
                grid: {
                    display: false, // Hide Y-axis grid lines
                },
                ticks: ticksObj,

                ...maxIndex
            },
            x: {
                title: {
                    display: false,
                    text: 'Timestamp'
                },
                grid: {
                    display: false, // Hide X-axis grid lines
                },
                ticks: {
                    font: {
                        family: "Inter"
                    },
                }
            }
        },
        elements: {
            line: {
                tension: 0.4, // Adjust curve tension (0.4 for moderate curve)
                borderWidth: 4, // Adjust line thickness
            },
            point: {
                radius: 0, // Remove data points
            },
        },
        animation: {
            x: {
                duration: 2000,
                from: 0
            },
            y: {
                duration: 2000,
                from: 500
            }
        },
        plugins: {
            legend: {
                position: "top",
                align: "start",
                labels: {
                    boxWidth: 30,
                    boxHeight: 5,
                    useBorderRadius: true,
                    borderRadius: 1,
                    font: {
                        family: "Inter"
                    },
                    // Anomaly legend: drop the invisible band edges, rename "Margin Zone"
                    // (outline its faint swatch so it's visible), list alphabetically.
                    generateLabels: dashboardType === "anomaly-monitoring"
                        ? (chart) => ChartJS.defaults.plugins.legend.labels
                            .generateLabels(chart)
                            .filter((item) => !["Margin Lower", "Lower Bound"].includes(item.text))
                            .map((item) => item.text === "Margin Zone"
                                ? { ...item, text: "Margin", strokeStyle: "#cfff5e", lineWidth: 1 }
                                : item)
                            .sort((a, b) => a.text.localeCompare(b.text))
                        : undefined,
                },
            },
            customMarginPlugin: {
                marginTop: 50,
            },
            tooltip: dashboardType === "anomaly-monitoring" ? {
                // Anomaly chart uses a custom HTML tooltip so the (potentially long)
                // list of anomalous tags is severity-ordered and scrollable.
                enabled: false,
                external: anomalyTooltipHandler,
            } : {
                callbacks: {
                    label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            if (dashboardType === "error-monitoring") {
                                label += context.parsed.y.toFixed(2); // Display value with 2 decimals
                                label += '%'; // Add percentage symbol for error-monitoring
                            } else {
                                label += Math.round(context.parsed.y); // Display value without decimals
                            }
                        }
                        return label;
                    }
                }
            }
        },
        tooltips: {
            mode: 'index',
            intersect: false
        },
        hover: {
            mode: 'index',
            intersect: false
        }
    }
};

export const consentChartOptions = () => {
    return {
        responsive: true,
        maintainAspectRatio: true,
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Percentage (%)',
                },
                grid: {
                    display: false, // Hide Y-axis grid lines
                },
                ticks: {
                    callback: function(value) {
                        return value; // Add percentage symbol to y-axis labels
                    },
                    font: {
                        family: "Inter"
                    },
                },
            },
            x: {
                title: {
                    display: false,
                    text: 'Timestamp'
                },
                grid: {
                    display: false, // Hide X-axis grid lines
                },
                ticks: {
                    font: {
                        family: "Inter"
                    },
                }
            }
        },
        elements: {
            line: {
                tension: 0.4, // Adjust curve tension (0.4 for moderate curve)
                borderWidth: 4, // Adjust line thickness
            },
            point: {
                radius: 0, // Remove data points
            },
        },
        plugins: {
            legend: {
                position: "top",
                align: "start",
                labels: {
                    boxWidth: 30,
                    boxHeight: 5,
                    useBorderRadius: true,
                    borderRadius: 1,
                    font: {
                        family: "Inter"
                    },
                },
            },
            customMarginPlugin: {
                marginTop: 50,
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += context.parsed.y.toFixed(2) + '%'; // Format tooltip label as percentage
                        }
                        return label;
                    }
                }
            }
        },
        tooltips: {
            mode: 'index',
            intersect: false
        },
        hover: {
            mode: 'index',
            intersect: false
        }
    }
};